<template>
  <div class="home">
    <div class="content-container">
        <blockquote class="quote-content" :class="{ loading: poemLoading }">
          <p v-if="poemLoading" class="loading-text">正在加载今日诗词....</p>
          <p v-else>{{ poem.content }}</p>
          <footer v-if="poem.origin && !poemLoading">
            - {{ poem.origin.author }}《{{ poem.origin.title }}》
          </footer>
        </blockquote>

      <section class="recent-posts">
        <!-- 悬浮搜索组件 -->
        <FloatingSearch
          :is-searching="isSearching"
          @search="handleSearchFromComponent" />

        <LoadingSpinner
          v-if="loading || isSearching"
          :text="isSearching ? '正在搜索...' : '正在加载文章...'" />
        <!-- 搜索状态显示 -->
        <div v-if="searchKeyword && !loading && !isSearching" class="search-status">
          <div class="search-results-header">
            <h2 class="search-results-title">
              <span class="search-icon">🔍</span>
              搜索结果
            </h2>
            <div class="search-stats">
              <span class="results-count">找到 {{ total }} 篇文章</span>
              <span class="search-keyword">关键词："{{ searchKeyword }}"</span>
            </div>
          </div>
          <el-button
            type="text"
            @click="clearSearch"
            class="clear-search-btn">
            <i class="el-icon-close"></i>
            清空搜索
          </el-button>
        </div>


        <ArticleList
          v-if="!loading && !isSearching && articles.length > 0"
          :articles="articles"
          :total="total"
          :page-size="pageSize"
          :current-page="currentPage"
          @page-change="handlePageChange"
          @category-click="handleCategoryClick" />

        <div
          v-else-if="!loading && !isSearching && articles.length === 0"
          class="empty-state"
          role="status">
          <p>{{ searchKeyword ? '没有找到相关文章' : '暂时还没有文章' }}</p>
          <el-button v-if="searchKeyword" type="text" @click="clearSearch">
            清空搜索并返回文章列表
          </el-button>
        </div>
      </section>
    </div>

    <footer class="site-footer">
      <p>© {{ new Date().getFullYear() }} x-shuffle 博客 All Rights Reserved.</p>
    </footer>
  </div>
</template>

<script>
import { getArticles, searchArticles } from '@/api'
import ArticleList from '@/components/ArticleList.vue'
import FloatingSearch from '@/components/FloatingSearch.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'

export default {
  name: 'Home',
  components: {
    ArticleList,
    FloatingSearch,
    LoadingSpinner
  },
  data() {
    return {
      articles: [],
      total: 0,
      pageSize: 10,
      currentPage: 1,

      loading: false,
      searchKeyword: '',
      isSearching: false,
      poem: {
        content: '',
        origin: null
      },
      poemLoading: true
    }
  },
  async created() {
    await this.fetchArticles()
    this.updatePageMeta()
  },
  methods: {
    async fetchArticles() {
      this.loading = true
      try {
        const res = await getArticles({
          pageSize: this.pageSize,
          pageNum: this.currentPage
        })
        this.articles = res.data || []
        this.total = res.total || 0
      } catch (error) {
        console.error('获取文章列表错误:', error)
        this.$message.error('获取文章列表失败')
      } finally {
        this.loading = false
      }
    },


    // 处理来自搜索组件的搜索事件
    handleSearchFromComponent(keyword) {
      this.searchKeyword = keyword
      this.performSearch()
    },

    // 执行搜索
    async performSearch() {
      if (!this.searchKeyword.trim()) {
        this.$message.warning('请输入搜索关键词')
        return
      }

      this.isSearching = true
      this.currentPage = 1

      try {
        const params = {
          keyword: this.searchKeyword.trim(),
          pageSize: this.pageSize,
          pageNum: this.currentPage
        }

        const response = await searchArticles(params)

        if (response && response.status === 200) {
          this.articles = response.data || []
          this.total = response.total || 0

          if (this.articles.length === 0) {
            this.$message.info('没有找到相关文章')
          } else {
            this.$message.success(`找到 ${this.total} 篇相关文章`)
          }
        } else {
          this.$message.error((response && response.message) || '搜索失败')
          this.articles = []
          this.total = 0
        }
      } catch (error) {
        console.error('搜索文章失败:', error)
        this.$message.error('搜索失败，请稍后重试')
        this.articles = []
        this.total = 0
      } finally {
        this.isSearching = false
      }
    },

    // 清空搜索
    clearSearch() {
      this.searchKeyword = ''
      this.currentPage = 1
      this.fetchArticles()
    },





    handlePageChange(page) {
      this.currentPage = page
      if (this.searchKeyword.trim()) {
        this.performSearch()
      } else {
        this.fetchArticles()
      }
    },


    handleCategoryClick(category) {
      if (category) {
        this.$router.push(`/categories?id=${category.id}`)
      }
    },
    loadPoem() {
      // 添加今日诗词 SDK
      const script = document.createElement('script')
      script.src = 'https://sdk.jinrishici.com/v2/browser/jinrishici.js'
      document.head.appendChild(script)

      // 等待 SDK 加载完成后获取诗词
      script.onload = () => {
        if (window.jinrishici && window.jinrishici.load) {
          try {
            const loadResult = window.jinrishici.load(result => {
              this.poem = {
                content: result.data.content,
                origin: result.data.origin
              }
              this.poemLoading = false
            })

            // 如果load方法返回Promise，则处理错误
            if (loadResult && typeof loadResult.catch === 'function') {
              loadResult.catch(error => {
                console.error('加载诗词失败:', error)
                this.poem.content = '今日诗词加载失败，但生活依然美好 🌸'
                this.poemLoading = false
              })
            }
          } catch (error) {
            console.error('加载诗词失败:', error)
            this.poem.content = '今日诗词加载失败，但生活依然美好 🌸'
            this.poemLoading = false
          }
        } else {
          console.error('诗词SDK未正确加载')
          this.poem.content = '今日诗词加载失败，但生活依然美好 🌸'
          this.poemLoading = false
        }
      }

      script.onerror = () => {
        console.error('诗词SDK加载失败')
        this.poem.content = '今日诗词加载失败，但生活依然美好 🌸'
        this.poemLoading = false
      }
    },
    updatePageMeta() {
      document.title = 'x-shuffle 博客 - 首页'
      const metaDescription = document.querySelector('meta[name="description"]')
      if (metaDescription) {
        metaDescription.setAttribute('content', 'x-shuffle 博客，分享技术、生活、思考。')
      }
    }
  },

  mounted() {
    this.loadPoem()
  },
  beforeDestroy() {
    // 清理 SDK script 标签
    const script = document.querySelector('script[src="https://sdk.jinrishici.com/v2/browser/jinrishici.js"]')
    if (script) {
      document.head.removeChild(script)
    }
  }
}
</script>

<style scoped>
.home {
  padding-top: clamp(0.5rem, 2vw, 1.25rem);
}

.content-container {
  width: min(calc(100% - 2rem), 960px);
  margin: 0 auto;
  padding: 0;
}

.daily-quote {
  margin-bottom: 2rem;
  padding: 2rem;
  background: #f5efe6;
  border: none;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease;
}

.daily-quote:hover {
  transform: translateY(-2px);
}

.quote-title {
  color: var(--text-color);
  font-size: 1rem;
  margin-bottom: 0.75rem;
}

.quote-content {
  color: var(--text-secondary);
  padding: 1rem;
  /* background: rgba(255, 255, 255, 0.5); */
  border-radius: 8px;
}

.quote-content p {
  font-size: 1.8rem;
  line-height: 1.8;
  margin-bottom: 1rem;
  text-align: center;
  color: #333;
  font-family: "STKaiti", "KaiTi", "楷体", "SimKai", serif;
  letter-spacing: 4px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.1);
}

.loading-text {
  opacity: 0.6;
  font-style: italic;
}

.quote-content.loading {
  opacity: 0.8;
}



.empty-state {
  text-align: center;
  padding: 3rem;
  color: #666;
  font-size: 1.1rem;
}



.quote-content footer {
  text-align: right;
  color: #666;
  font-size: 1.2rem;
  margin-top: 1.5rem;
  font-family: "STKaiti", "KaiTi", "楷体", "SimKai", serif;
}

.section-title {
  color: var(--text-color);
  font-size: 2rem;
  margin-bottom: 2rem;
  font-weight: 700;
}

.post-list {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  max-width: 800px;
  margin: 0 auto;
}

.post-card {
  background: var(--card-bg);
  border: none;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease;
}

.post-card:hover {
  transform: translateY(-4px);
}

.post-category {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background-color: #e8e1d5;
  color: #6b5f4c;
  border-radius: 16px;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  transition: all 0.2s ease;
  cursor: pointer;
}

.post-category:hover {
  background-color: #e0d9cd;
  transform: translateY(-1px);
}

.post-title {
  font-size: 1.5rem;
  margin: 1rem 0;
}

.post-title a {
  color: #2d3748;  /* 深色文字基础颜色 */
  text-decoration: none;
  position: relative;
  transition: all 0.3s ease;
  font-weight: 600;
}

.post-title a::after {
  content: '';
  position: absolute;
  width: 100%;
  height: 2px;
  bottom: -2px;
  left: 0;
  background-color: #6b5f4c;  /* 暖棕色下划线，与分类标签颜色呼应 */
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s ease;
}

.post-title a:hover {
  color: #4a5568;  /* 悬浮时稍微变浅 */
}

.post-title a:hover::after {
  transform: scaleX(1);
}

.post-excerpt {
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 1rem 0;
}

.post-meta {
  color: var(--text-secondary);
  font-size: 0.9rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border-color);
}



.pagination {
  margin: 2rem 0;
  display: flex;
  justify-content: center;
}



@media (max-width: 768px) {
  .home {
    padding-top: 0;
  }

  .content-container {
    width: min(calc(100% - 1.5rem), 960px);
    padding: 0;
  }

  .post-card {
    padding: 1.5rem;
  }

  .post-title {
    font-size: 1.5rem;
  }

  .post-excerpt {
    font-size: 1rem;
  }
}

.site-footer {
  margin-top: 4rem;
  padding: 2rem;
  text-align: center;
  color: var(--text-secondary);
  border-top: 1px solid var(--border-color);
}

@media (max-width: 768px) {
  .site-footer {
    margin-top: 2rem;
    padding: 1rem;
  }
}



.daily-quote,
.post-card {
  backdrop-filter: none;
}

/* 悬浮搜索按钮样式 */
.floating-search-btn {
  position: fixed;
  left: 2rem;
  top: 50%;
  transform: translateY(-50%);
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #6b5f4c 0%, #8b7d6b 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(107, 95, 76, 0.3);
  transition: all 0.3s ease;
  color: #f5f0e8;
  font-size: 1.5rem;
  z-index: 1000;
  animation: float 3s ease-in-out infinite;
}

.floating-search-btn:hover {
  transform: translateY(-50%) scale(1.1);
  box-shadow: 0 8px 30px rgba(107, 95, 76, 0.5);
  animation-play-state: paused;
}

.floating-search-btn i {
  transition: transform 0.3s ease;
}

.floating-search-btn:hover i {
  transform: scale(1.1);
}

@keyframes float {
  0%, 100% {
    transform: translateY(-50%) translateX(0);
  }
  50% {
    transform: translateY(-50%) translateX(5px);
  }
}

/* 搜索状态样式 */
.search-status {
  background: var(--card-bg);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid var(--border-color);
}

.search-results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.search-results-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  font-size: 1.5rem;
  color: var(--text-color);
}

.search-icon {
  font-size: 1.2rem;
}

.search-stats {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
}

.results-count {
  font-size: 1rem;
  color: var(--text-color);
  font-weight: 600;
}

.search-keyword {
  font-size: 0.9rem;
  color: var(--text-secondary);
}

.clear-search-btn {
  color: #6b5f4c;
  font-size: 0.9rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.clear-search-btn:hover {
  background: rgba(107, 95, 76, 0.1);
  color: #8b7d6b;
}

.empty-actions {
  margin-top: 1.5rem;
}

.empty-actions .el-button {
  background: #6b5f4c;
  border-color: #6b5f4c;
  color: #f5f0e8;
}

.empty-actions .el-button:hover {
  background: #8b7d6b;
  border-color: #8b7d6b;
}

/* 搜索模态框样式 */
.search-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(4px);
}

.search-modal {
  background: var(--card-bg);
  border-radius: 16px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.search-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem 1rem;
  border-bottom: 1px solid var(--border-color);
}

.search-modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  color: var(--text-color);
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.2rem;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: rgba(0, 0, 0, 0.1);
  color: var(--text-color);
}

.search-modal-content {
  padding: 1.5rem 2rem 2rem;
}

.search-input-container {
  margin-bottom: 2rem;
}

.search-input-container .el-input__inner {
  border-radius: 12px;
  border: 2px solid var(--border-color);
  background: var(--bg-color);
  font-size: 1rem;
  padding: 0.75rem 1rem;
  transition: all 0.3s ease;
}

.search-input-container .el-input__inner:focus {
  border-color: #6b5f4c;
  box-shadow: 0 0 0 3px rgba(107, 95, 76, 0.1);
}

.search-input-container .el-button {
  background: #6b5f4c;
  border-color: #6b5f4c;
  color: #f5f0e8;
  border-radius: 0 12px 12px 0;
  padding: 0.75rem 1.5rem;
  transition: all 0.3s ease;
}

.search-input-container .el-button:hover {
  background: #8b7d6b;
  border-color: #8b7d6b;
  transform: translateY(-1px);
}



/* 响应式设计 */
@media (max-width: 768px) {
  .search-modal {
    width: 95%;
    margin: 1rem;
  }

  .search-modal-header,
  .search-modal-content {
    padding: 1rem 1.5rem;
  }
}
</style>
